<template>
  <div class="be-promo" v-if="hasPromotion">
    <div class="be-promo__head">
      <div class="be-promo__head-text">
        <h3 class="be-promo__title">
          Special Offers
          <span class="be-promo__count">{{ promotionList.length }}</span>
        </h3>
        <p class="be-promo__subtitle">Pick a deal before you choose your room.</p>
      </div>
      <div class="be-promo__nav" v-if="isUseSlider">
        <button type="button" class="be-promo__nav-btn be-press" aria-label="Previous offer" @click="goPrevious">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="m14.5 5-7 7 7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <button type="button" class="be-promo__nav-btn be-press" aria-label="Next offer" @click="goNext">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="m9.5 5 7 7-7 7" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>

    <flickity
      v-if="isUseSlider"
      ref="flickity"
      :options="sliderConfig"
      class="be-promo__slider">
      <div
        class="carousel-cell be-promo__cell"
        v-for="(promotion, promotionIndex) in promotionList"
        :key="promotion.id">
        <PromotionCard
          @select="selectPromotion"
          @apply="applyPromotion"
          :promotion="promotion"
          :promotionIndex="promotionIndex" />
      </div>
    </flickity>

    <PromotionCard
      v-else
      @select="selectPromotion"
      @apply="applyPromotion"
      :promotion="promotionList[0]"
      :promotionIndex="0" />

    <b-modal
      size="sm"
      hide-header
      hide-footer
      body-class="p-0"
      v-model="showDetailPopup">
      <PromotionCard
        v-if="$isNotEmptyV2(promotionDetailSelected)"
        :showDetail="true"
        @apply="applyPromotion"
        @closeDetail="closeDetailPromotion"
        :promotion="promotionDetailSelected" />
    </b-modal>
  </div>
</template>

<script>
  import { get } from '@/services/index.js';
  import { mapGetters, mapMutations } from 'vuex';
  import Flickity from 'vue-flickity';
  import PromotionCard from './promotion/PromotionCard.vue';
  import {
    parseDiscountAmount,
    generatePromotionRule
  } from './promotion/promotionFormula.js';

  const MOBILE_BREAKPOINT = 577;

  export default {
    name: 'BePotentialPromotion',
    components: {
      Flickity,
      PromotionCard
    },
    data(){
      return {
        isLoading: true,
        sliderConfig: {
          initialIndex: 0,
          autoPlay: 5000,
          prevNextButtons: false,
          pageDots: true,
          adaptiveHeight: false,
          draggable: false,
          wrapAround: false,
          contain: true
        },
        sliderElement: null,
        promotionList: [],
        showDetailPopup: false,
        promotionDetailSelected: {}
      }
    },
    computed: {
      ...mapGetters(['getMerchant', 'getBeFilter', 'getSelectedPromotion']),
      isUseSlider(){
        return this.promotionList.length > 1;
      },
      hasPromotion(){
        return !this.isLoading && this.promotionList.length > 0;
      }
    },
    created(){
      if(window.innerWidth < MOBILE_BREAKPOINT){
        this.sliderConfig.pageDots = false;
        this.sliderConfig.draggable = true;
      }
    },
    async mounted(){
      await this.loadPromotionList();
      if(this.isUseSlider) this.initSlider();
    },
    methods: {
      ...mapMutations(['setSelectedPromotion', 'resetSelectedPromotion', 'setSpecificBeFilter']),
      initSlider(){
        this.$nextTick(() => {
          this.sliderElement = this.$refs.flickity ? this.$refs.flickity : null;
        });
      },
      // wrapped navigation keeps the arrows usable on the first and last offer
      goPrevious(){
        if(this.sliderElement === null) return;
        this.sliderElement.previous(true);
      },
      goNext(){
        if(this.sliderElement === null) return;
        this.sliderElement.next(true);
      },
      getPromotionApiParams(){
        const { check_in, check_out } = this.getBeFilter.date;
        return {
          merchant_id: this.getMerchant.id,
          check_in_date: this.$formatDateApi(check_in),
          check_out_date: this.$formatDateApi(check_out)
        };
      },
      async loadPromotionList(){
        try {
          this.isLoading = true;
          this.promotionList = [];

          const apiParams = this.getPromotionApiParams();
          const apiresponse = await get('CORE_HOTEL', 'search-room/promotion', apiParams);
          if(!apiresponse.data.success) throw new Error(apiresponse.data.message);

          const formatAmount = (value) => this.$formatCurrencyCompact(value);
          this.promotionList = parseDiscountAmount(apiresponse.data.data, formatAmount);
          this.promotionList = this.sortingPromotionList(this.promotionList);
          this.promotionList = generatePromotionRule(this.promotionList);
          return Promise.resolve();
        } catch (error) {
          console.log('ERROR in loadPromotionList() (BePotentialPromotion.vue) : ', error);
        } finally {
          this.isLoading = false;
        }
      },
      sortingPromotionList(promotionList){
        if(this.$isEmpty(this.getSelectedPromotion)) return promotionList;

        const promotionIndex = promotionList.findIndex(
          (promotion) => promotion.id === this.getSelectedPromotion.id
        );
        if(promotionIndex === -1){
          this.resetSelectedPromotion();
          return promotionList;
        }

        const newPromotionList = promotionList;
        const [activePromotion] = newPromotionList.splice(promotionIndex, 1);
        newPromotionList.unshift(activePromotion);
        return newPromotionList;
      },
      selectPromotion(index){
        if(this.isUseSlider){
          const activeSlide = this.sliderElement.selectedIndex();
          if(index != activeSlide) this.sliderElement.select(index);
          else this.openDetailPromotion(index);
        }else{
          this.openDetailPromotion(index);
        }
      },
      applyPromotion(index){
        const isFromDetail = index === -2;
        const selectedPromotion = isFromDetail
          ? this.promotionDetailSelected
          : this.promotionList[index];
        if(isFromDetail || !this.isUseSlider){
          this.saveSelectedPromotion(selectedPromotion);
        }else{
          const activeSlide = this.sliderElement.selectedIndex();
          if(index === activeSlide) this.saveSelectedPromotion(selectedPromotion);
          else this.selectPromotion(index);
        }
      },
      saveSelectedPromotion(selectedPromotion){
        const { promotion_type, promo_code } = selectedPromotion;
        const promoCode = promotion_type === 'PROMO_CODE' ? promo_code : '';
        this.setSpecificBeFilter([{ key: 'promo_code', value: promoCode }]);

        this.setSelectedPromotion(selectedPromotion);
      },

      // DETAIL
      openDetailPromotion(index){
        const selectedPromotion = this.promotionList[index];
        if(this.$isEmpty(selectedPromotion)) return;

        this.promotionDetailSelected = selectedPromotion;
        this.toggleDetailPopup(true);
      },
      closeDetailPromotion(){
        this.promotionDetailSelected = {};
        this.toggleDetailPopup();
      },
      toggleDetailPopup(show = false){
        this.showDetailPopup = show;
      }
    }
  }
</script>

<style scoped>
.be-promo {
  --be-v3-text-caption: 0.75rem;
  --be-v3-text-secondary: 0.8125rem;
  --be-v3-promo-border: #d5d9e0;
  width: 100%;
  margin-bottom: 20px;
}
.be-promo__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.be-promo__head-text {
  min-width: 0;
}
.be-promo__title {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.3;
  color: #1a1a2e;
  margin: 0 0 1px;
}
.be-promo__subtitle {
  font-size: var(--be-v3-text-caption);
  color: #888;
  line-height: 1.4;
  margin: 0;
}
.be-promo__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 19px;
  height: 19px;
  padding: 0 6px;
  margin-left: 2px;
  vertical-align: 2px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.05);
  color: #666;
  font-size: 0.6875rem;
  font-weight: 600;
}
.be-promo__nav {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.be-promo__nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--be-v3-promo-border);
  border-radius: 50%;
  background: #fff;
  color: #555;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
}
.be-promo__nav-btn:hover {
  border-color: var(--merchant-primary-color, #79a33a);
  color: var(--merchant-primary-color, #79a33a);
}
.be-promo__slider {
  padding-bottom: 20px;
}
.be-promo__cell {
  width: 100%;
  padding: 2px;
}
.be-promo >>> .flickity-page-dots {
  bottom: 0;
  line-height: 1;
}
.be-promo >>> .flickity-page-dots .dot {
  width: 6px;
  height: 6px;
  margin: 0 3px;
  background: var(--be-v3-promo-border);
  opacity: 1;
  transition: width 0.2s ease, background 0.2s ease;
}
.be-promo >>> .flickity-page-dots .dot.is-selected {
  width: 15px;
  border-radius: 3px;
  background: var(--merchant-primary-color, #79a33a);
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BePotentialPromotion.vue